/**
 * Hook to track unread message count for the chat widget badge.
 *
 * Uses localStorage to persist the last seen message ID across page refreshes.
 * Only counts assistant messages (AI or human agent replies) as unread.
 */

import { useCallback, useRef, useSyncExternalStore } from 'react'
import type { WidgetMessage } from '../types'

function getStorageKey(visitorId: string): string {
    return `chat_widget_${visitorId}_lastSeen`
}

function readLastSeenId(visitorId: string): string | null {
    try {
        return localStorage.getItem(getStorageKey(visitorId))
    } catch {
        return null
    }
}

function writeLastSeenId(visitorId: string, messageId: string): void {
    try {
        localStorage.setItem(getStorageKey(visitorId), messageId)
    } catch {
        // localStorage may be unavailable (e.g. private browsing quota exceeded)
    }
}

/**
 * A small external store backed by localStorage for the last seen message ID.
 * useSyncExternalStore ensures React re-renders when the value changes
 * without needing setState inside effects.
 */
function useLastSeenStore(visitorId: string) {
    const listenersRef = useRef(new Set<() => void>())

    const subscribe = useCallback((listener: () => void) => {
        listenersRef.current.add(listener)
        return () => { listenersRef.current.delete(listener) }
    }, [])

    const getSnapshot = useCallback(() => {
        if (!visitorId) return null
        return readLastSeenId(visitorId)
    }, [visitorId])

    const setLastSeenId = useCallback((messageId: string) => {
        if (!visitorId) return
        writeLastSeenId(visitorId, messageId)
        // Notify subscribers so useSyncExternalStore re-renders
        listenersRef.current.forEach((listener) => listener())
    }, [visitorId])

    const lastSeenId = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

    return { lastSeenId, setLastSeenId }
}

export function useUnreadCount(
    displayedMessages: WidgetMessage[],
    isOpen: boolean,
    visitorId: string,
): number {
    const { lastSeenId, setLastSeenId } = useLastSeenStore(visitorId)

    // Only track real Firestore messages — exclude the local greeting
    const assistantMessages = displayedMessages.filter(
        (msg) => msg.sender === 'assistant' && !msg.id.startsWith('greeting-'),
    )

    // Mark latest assistant message as seen when widget is open
    if (isOpen && visitorId && assistantMessages.length > 0) {
        const latestId = assistantMessages[assistantMessages.length - 1].id
        if (latestId !== lastSeenId) {
            setLastSeenId(latestId)
        }
    }

    // Compute unread count — pure derivation from props + external store
    if (!visitorId || isOpen) return 0

    if (assistantMessages.length === 0) return 0

    // No lastSeenId (first visit or localStorage cleared)
    // → reset to current position, don't show a false badge
    if (!lastSeenId) {
        const latest = assistantMessages[assistantMessages.length - 1]
        if (latest) setLastSeenId(latest.id)
        return 0
    }

    // Find the index of the last seen message
    const lastSeenIndex = assistantMessages.findIndex(
        (msg) => msg.id === lastSeenId,
    )

    // lastSeenId not found in current messages (stale/deleted)
    // → reset to current position, don't show a false badge
    if (lastSeenIndex === -1) {
        const latest = assistantMessages[assistantMessages.length - 1]
        if (latest) setLastSeenId(latest.id)
        return 0
    }

    // Count assistant messages after the last seen one
    return assistantMessages.length - 1 - lastSeenIndex
}
